import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ChevronDown, LogOut } from 'lucide-react'
import { useEffect, useState } from 'react'
import { type Feedback, FeedbackBanner } from '@/components/app/feedback-banner'
import { QrScanner } from '@/components/app/qr-scanner'
import { Screen } from '@/components/app/screen'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { useLogout } from '@/hooks/use-logout'
import { useWakeLock } from '@/hooks/use-wake-lock'
import { apiPost } from '@/lib/api'
import { getApiErrorMessage } from '@/lib/api-error'
import { SERVICE_TYPE_LABELS } from '@/lib/constants'
import { formatDateFR } from '@/lib/format'
import { recentServicesQuery, type Service } from '@/lib/queries/services'

export const Route = createFileRoute('/_placeur/scanner/')({
  component: ScannerScreen,
})

interface ScanResult {
  member: { firstName: string; lastName: string; memberNumber: string }
}

function ScannerScreen() {
  const logout = useLogout()
  const [selected, setSelected] = useState<Service | null>(null)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [feedback, setFeedback] = useState<Feedback | null>(null)

  const { data: services } = useQuery(recentServicesQuery(10))

  // Le culte le plus récent est le cas courant : on le présélectionne.
  useEffect(() => {
    if (!selected && services?.data.length) setSelected(services.data[0])
  }, [services, selected])

  // Le placeur ne touche pas l'appareil entre deux fidèles.
  useWakeLock(true)

  async function handleDetect(qrCode: string) {
    if (!selected) return

    setFeedback({ kind: 'pending', message: 'Vérification…' })

    try {
      const res = await apiPost<ScanResult>(`services/${selected.id}/attendances/qr`, { qrCode })
      setFeedback({
        kind: 'success',
        message: `${res.member.firstName} ${res.member.lastName} — présent`,
      })
    } catch (err) {
      setFeedback({
        kind: 'error',
        message: getApiErrorMessage(err, 'QR code non reconnu.'),
      })
    }
  }

  return (
    <Screen className="flex flex-col gap-4 px-5 pb-5">
      <header className="flex items-start justify-between gap-4 pt-6">
        <div>
          <p className="text-[11px] font-semibold tracking-[1.2px] text-gold">PLACEUR</p>
          <h1 className="text-[26px] font-bold text-chalk">Scanner</h1>
        </div>
        <button
          type="button"
          onClick={logout}
          aria-label="Se déconnecter"
          className="p-2 text-danger"
        >
          <LogOut size={20} aria-hidden="true" />
        </button>
      </header>

      <Sheet open={pickerOpen} onOpenChange={setPickerOpen}>
        <SheetTrigger asChild>
          <button
            type="button"
            className="flex w-full items-center justify-between gap-3 rounded-[14px] border border-navy-800 bg-navy-900 px-4 py-3.5 text-left"
          >
            <span className="min-w-0">
              <span className="block text-[11px] tracking-[0.8px] text-steel uppercase">Culte</span>
              <span className="block truncate text-[15px] font-semibold text-chalk">
                {selected
                  ? `${SERVICE_TYPE_LABELS[selected.type] ?? selected.type} — ${formatDateFR(selected.date, { day: 'numeric', month: 'long' })}`
                  : 'Sélectionnez un culte'}
              </span>
            </span>
            <ChevronDown size={18} color="#8A9BB0" aria-hidden="true" />
          </button>
        </SheetTrigger>

        <SheetContent side="bottom" className="max-h-[70dvh] border-navy-800 bg-navy-900">
          <SheetHeader>
            <SheetTitle className="text-chalk">Choisir un culte</SheetTitle>
          </SheetHeader>

          <ul className="flex flex-col gap-2 overflow-y-auto px-4 pb-6">
            {services?.data.map((service) => (
              <li key={service.id}>
                <button
                  type="button"
                  onClick={() => {
                    setSelected(service)
                    setFeedback(null)
                    setPickerOpen(false)
                  }}
                  className="w-full rounded-[14px] border border-navy-800 px-4 py-3 text-left"
                >
                  <span className="block text-[15px] font-semibold text-chalk">
                    {SERVICE_TYPE_LABELS[service.type] ?? service.type}
                  </span>
                  <span className="block text-[13px] text-steel">
                    {formatDateFR(service.date, {
                      weekday: 'long',
                      day: 'numeric',
                      month: 'long',
                    })}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </SheetContent>
      </Sheet>

      {/* La caméra ne démarre qu'une fois le culte choisi : sans lui, un scan
          réussi n'aurait nulle part où être enregistré. */}
      {selected ? (
        <QrScanner onDetect={handleDetect} variant="full" />
      ) : (
        <div className="flex flex-1 items-center justify-center rounded-[20px] border border-navy-800 bg-navy-900 px-8">
          <p className="text-center text-[14px] text-steel">
            Sélectionnez un culte pour commencer à scanner.
          </p>
        </div>
      )}

      <FeedbackBanner
        feedback={feedback}
        idleMessage="Pointez la caméra vers le QR code du fidèle."
      />
    </Screen>
  )
}
